import { useCallback, useState } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { SearchIcon } from "@l2r-front/l2r-icons"
import { AutocompleteRenderInputParams, ClickAwayListener, createFilterOptions, useIsMobileDevice, useTheme } from "@l2r-front/l2r-ui"
import { isDDMLocation, isDMSLocation, isGpsLocation, transformDDMToDecimal, transformDMSToDecimal } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { useNetworksContext } from "../../contexts/NetworksContext"

import * as Styled from "./RoadSearch.styled"

export type GPSValue = {
    type: "gps",
    value: [number, number]
}

export type RoadValue = {
    type: "road",
    value: string
}

export type RoadSearchValue = GPSValue | RoadValue

type RoadSearchProps = {
    className?: string,
    disableExpend?: boolean,
    onValueChanged: (value: RoadSearchValue) => void,
    options: Array<{
        label: string,
    }>,
}

type RoadOption = {
    label: string,
    value: string,
}

export function RoadSearch(props: RoadSearchProps) {

    const {
        className,
        disableExpend = true,
        onValueChanged,
        options,
    } = props

    const isMobile = useIsMobileDevice()
    const theme = useTheme()
    const { t } = useTranslation(I18N_NAMESPACE)
    const [open, setOpen] = useState(false)
    const [displayAutoComplete, setDisplayAutoComplete] = useState(disableExpend)
    const [networksState, networksDispatch] = useNetworksContext()
    const { roadSearched } = networksState
    const { setRoadSearched } = networksDispatch
    const emptyValue: RoadOption = { label: "", value: "" }
    const [inputValue, setInputValue] = useState<RoadOption>(roadSearched ? { label: roadSearched, value: roadSearched } : emptyValue)

    const handleChange = useCallback((_, rawValue) => {
        const value = typeof rawValue === "string" ? rawValue : rawValue?.label
        if (displayAutoComplete && !disableExpend) {
            setDisplayAutoComplete(false)
        }

        if (value) {
            setInputValue({ label: value, value: value })
            if (isDMSLocation(value)) {
                const [lng, lat] = transformDMSToDecimal(value)
                return onValueChanged({
                    type: "gps",
                    value: [lng, lat],
                })
            }
            if (isDDMLocation(value)) {
                const [lng, lat] = transformDDMToDecimal(value)
                return onValueChanged({
                    type: "gps",
                    value: [lng, lat],
                })
            }

            if (isGpsLocation(value)) {
                const [lng, lat] = value.split(",").map(v => parseFloat(v.trim()))
                return onValueChanged({
                    type: "gps",
                    value: [lng, lat],
                })
            } else {
                return onValueChanged({
                    type: "road",
                    value
                })
            }
        }
    }, [disableExpend, displayAutoComplete, onValueChanged])

    return (
        <ClickAwayListener onClickAway={(event) => {
            if (displayAutoComplete && !disableExpend) {
                event.preventDefault()
                setDisplayAutoComplete(false)
                setInputValue(roadSearched ? { label: roadSearched, value: roadSearched } : emptyValue)
            }
            setOpen(false)
        }}>
            <Styled.Wrapper displayAutoComplete={displayAutoComplete} isMobile={isMobile} onClick={() => {
                if (disableExpend || displayAutoComplete) {
                    setOpen(v => !v)
                }
            }}>
                {!displayAutoComplete && <Styled.IconButton
                    className={className}
                    id="search-road-button"
                    variant="tertiary"
                    shape="circle"
                    outlined={false}
                    isMobile={isMobile}
                    onClick={() => setDisplayAutoComplete(true)}>
                    <SearchIcon />
                </Styled.IconButton>
                }
                {displayAutoComplete && <Styled.Autocomplete
                    open={open}
                    className={className}
                    id="search-road-select"
                    isMobile={isMobile}
                    blurOnSelect={true}
                    selectOnFocus={false}
                    options={options}
                    isOptionEqualToValue={(option: RoadOption, value: string) => {
                        return option.label === value
                    }}
                    filterOptions={(options, params) => {
                        const option: RoadOption = (inputValue)
                        if (isGpsLocation(option.value) || isDMSLocation(option.value) || isDDMLocation(option.value)) {
                            return [{ label: option.value, value: option.value }]
                        }

                        return createFilterOptions()(options, params)
                    }}
                    noOptionsText={t(I18N_NAMESPACE, "components.roadSearch.noOptions")}
                    popupIcon={null}
                    onChange={handleChange}
                    onInputChange={(_, newInputValue) => {
                        setOpen(true)
                        setInputValue({ label: newInputValue, value: newInputValue })
                    }}
                    value={inputValue || emptyValue}
                    renderInput={(params: AutocompleteRenderInputParams) => {
                        return (
                            <Styled.TextField
                                {...params}
                                color="primary"
                                size="small"
                                variant="outlined"
                                value={inputValue || ""}
                                placeholder={t(I18N_NAMESPACE, "components.roadSearch.placeholder")}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <SearchIcon />
                                        ),
                                        endAdornment: (
                                            inputValue?.label?.length > 0 ? <Styled.CloseIcon
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    setRoadSearched(null)
                                                    return setInputValue(emptyValue)
                                                }}
                                            /> : null
                                        ),
                                    },
                                }}
                            />
                        )
                    }}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: "offset",
                                    options: {
                                        offset: [0, 8],
                                    },
                                },
                            ],
                        },
                        paper: {
                            sx: {
                                boxShadow: theme.customShadow("LCS/Shadow-dropdown"),
                                borderRadius: 3,
                            },
                        },
                    }}
                />
                }
            </Styled.Wrapper>
        </ClickAwayListener>
    )
}